<template>
  <div id="my-sponsorship" class="content-section">
    <!-- Page Title (always visible) -->
    <h1 class="page-title">Le Parrainage</h1>

    <!-- Tabs Navigation -->
    <div class="sponsorship-tabs">
      <a
        href="#"
        :class="['tab-link', { 'active': activeTab === null }]"
        @click.prevent="activeTab = null">Fonctionnement
      </a>
      <a
        href="#"
        :class="['tab-link', { 'active': activeTab === 'points' }]"
        @click.prevent="activeTab = 'points'"
      >Points gagnés</a>
      <a
        href="#"
        :class="['tab-link', { 'active': activeTab === 'referral' }]"
        @click.prevent="activeTab = 'referral'"
      >Parrainer un proche</a>
    </div>

    <!-- DEFAULT VIEW (no tab selected) -->
    <div v-if="activeTab === null" class="sponsorship-card">
      <p class="intro-text">
        Grâce au parrainage, cumulez des points de fidélité et profitez de codes exclusifs en invitant vos proches à rejoindre la communauté du Club Hortense.
      </p>

      <h2 class="how-it-works-title">COMMENT ÇA MARCHE ?</h2>

      <!-- Desktop View -->
      <div v-if="!isPhone" class="steps-container">
        <!-- Badges Row -->
        <div class="steps-badges-row">
          <div class="step-badge">ÉTAPE 1</div>
          <div class="step-dashed-line"></div>
          <div class="step-badge">ÉTAPE 2</div>
          <div class="step-dashed-line"></div>
          <div class="step-badge">ÉTAPE 3</div>
        </div>

        <!-- Cards Row -->
        <div class="steps-cards-row">
          <!-- Step 1 Card -->
          <div class="step-col">
            <div class="step-card">
              <h3 class="step-title">Invitez vos proches</h3>
              <p class="step-desc">Envoyez-leur votre lien de parrainage unique depuis votre espace personnel.</p>
            </div>
          </div>

          <!-- Step 2 Card -->
          <div class="step-col">
            <div class="step-card">
              <h3 class="step-title">Votre filleul s'inscrit</h3>
              <p class="step-desc">Votre filleul reçoit <strong>100 points</strong> à l'inscription, et vous gagnez <strong>50 points</strong> pour chaque parrainage.</p>
            </div>
          </div>

          <!-- Step 3 Card -->
          <div class="step-col">
            <div class="step-card">
              <h3 class="step-title">Ils réservent, vous en profitez</h3>
              <p class="step-desc">Dès la première réservation de votre filleul, <strong>recevez un code exclusif de -10% pour votre prochain séjour.</strong></p>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile View (Vertical Stack) -->
      <div v-if="isPhone" class="steps-container-mobile">
        <!-- Step 1 -->
        <div class="step-mobile-group">
          <div class="step-badge-mobile">ÉTAPE 1</div>
          <div class="step-card">
            <h3 class="step-title">Invitez vos proches</h3>
            <p class="step-desc">Envoyez-leur votre lien de parrainage unique depuis votre espace personnel.</p>
          </div>
        </div>

        <!-- Step 2 -->
        <div class="step-mobile-group">
          <div class="step-badge-mobile">ÉTAPE 2</div>
          <div class="step-card">
            <h3 class="step-title">Votre filleul s'inscrit</h3>
            <p class="step-desc">Votre filleul reçoit <strong>100 points</strong> à l'inscription, et vous gagnez <strong>50 points</strong> pour chaque parrainage.</p>
          </div>
        </div>

        <!-- Step 3 -->
        <div class="step-mobile-group">
          <div class="step-badge-mobile">ÉTAPE 3</div>
          <div class="step-card">
            <h3 class="step-title">Ils réservent, vous en profitez</h3>
            <p class="step-desc">Dès la première réservation de votre filleul, <strong>recevez un code exclusif de -10% pour votre prochain séjour.</strong></p>
          </div>
        </div>
      </div>

      <div class="btn-center">
        <button @click="activeTab = 'referral'" class="hortense-btn-primary">JE PARRAINE UN PROCHE</button>
      </div>

      <div class="ou-separator">
        <span class="ou-text">OU</span>
      </div>

      <div class="btn-center">
        <a
          v-if="!isPhone"
          href="#"
          class="referral-link"
          @click.prevent="copyReferralLink"
        >
          {{ linkCopied ? 'Lien copié !' : "J'envoie un lien de parrainage" }}
        </a>
        <a
          v-else
          href="#"
          class="referral-link"
          @click.prevent="showShareOverlay = true"
        >
          J'envoie un lien de parrainage
        </a>
      </div>
    </div>

    <!-- Mobile Share Overlay (bottom sheet) -->
    <transition name="overlay-slide">
      <div v-if="showShareOverlay && isPhone" class="share-overlay-backdrop" @click.self="showShareOverlay = false">
        <div class="share-overlay-sheet">
          <button class="share-overlay-close" @click="showShareOverlay = false" aria-label="Fermer">&#x2715;</button>
          <h2 class="share-overlay-title">Partage l'expérience Hortense</h2>
          <p class="share-overlay-points">Reçois 50 points de fidélité</p>
          <p class="share-overlay-desc">
            Invite tes proches à rejoindre le Club Hortense grâce à ton lien personnalisé. Une fois que cette personne deviendra membre, tu recevras <strong>50 points de fidélité</strong> et elle recevra <strong>100 points de fidélité</strong>.
          </p>
          <button class="hortense-btn-primary share-overlay-cta" @click="shareReferralLink">
            Invitez un proche
          </button>
        </div>
      </div>
    </transition>

    <!-- POINTS GAGNÉS TAB -->
    <div v-if="activeTab === 'points'" class="sponsorship-card">
      <h2 class="card-section-title">Points gagnés</h2>
      <p class="card-section-desc">Découvrez vos points gagnés en tant que parrain et filleul, ainsi que vos codes exclusifs.</p>

      <div class="points-cards-grid">
        <!-- Parrain Points Card -->
        <div class="points-card">
          <div class="points-card-header mx-3">
            <span class="points-card-label">POINTS PARRAIN</span>
          </div>
          <div class="points-card-body">
            <div class="points-value">{{ sponsorPoints }}</div>
            <div class="points-unit">POINTS</div>
            <div class="points-desc">
              {{ sponsorCount }} proche(s) parrainé(s)<br>
              {{ completedSponsorships }} parrainage(s) complété(s)
            </div>
          </div>
        </div>

        <!-- Filleul Points Card -->
        <div class="points-card">
          <div class="points-card-header  mx-3">
            <span class="points-card-label">POINTS FILLEUL</span>
          </div>
          <div class="points-card-body">
            <div class="points-value">{{ godchildPoints }}</div>
            <div class="points-unit">POINTS</div>
            <div class="points-desc">Vous n'avez pas été parrainé</div>
          </div>
        </div>

        <!-- Exclusive Codes Card -->
        <div class="points-card">
          <div class="points-card-header  mx-3">
            <span class="points-card-label">CODES EXCLUSIFS</span>
          </div>
          <div class="points-card-body">
            <div v-if="exclusiveCodes && exclusiveCodes.length > 0" class="codes-list">
              <div v-for="code in exclusiveCodes" :key="code.id" class="code-item">
                <span class="code-text">{{ code.code }}</span>
              </div>
            </div>
            <div v-else class="no-codes">
              <p class="no-codes-main">Aucun code disponible</p>
              <p class="no-codes-sub">Parrainez des amis<br>pour gagner des codes !</p>
            </div>
          </div>
        </div>
      </div>

      <div class="btn-center">
        <button @click="activeTab = 'referral'" class="hortense-btn-primary">JE PARRAINE UN PROCHE</button>
      </div>
    </div>

    <!-- PARRAINER UN PROCHE TAB -->
    <div v-if="activeTab === 'referral'" class="sponsorship-card">
      <h2 class="card-section-title">Parrainez un proche</h2>
      <p class="card-section-desc">Invitez vos proches à découvrir Hortense et bénéficiez d'avantages exclusifs.</p>

      <form @submit.prevent="submitReferral" class="referral-form mx-auto">
        <div class="form-row">
          <div class="form-group">
            <label for="referral_first_name" class="form-label">Prénom*</label>
            <input
              type="text"
              id="referral_first_name"
              v-model="referralForm.referral_first_name"
              class="form-control"
              :class="{ 'is-invalid': errors.referral_first_name }"
              required
            />
            <div v-if="errors.referral_first_name" class="invalid-feedback">
              {{ errors.referral_first_name }}
            </div>
          </div>

          <div class="form-group">
            <label for="referral_last_name" class="form-label">Nom*</label>
            <input
              type="text"
              id="referral_last_name"
              v-model="referralForm.referral_last_name"
              class="form-control"
              :class="{ 'is-invalid': errors.referral_last_name }"
              required
            />
            <div v-if="errors.referral_last_name" class="invalid-feedback">
              {{ errors.referral_last_name }}
            </div>
          </div>
        </div>

        <div class="form-group form-group-full">
          <label for="referral_email" class="form-label">Email*</label>
          <input
            type="email"
            id="referral_email"
            v-model="referralForm.referral_email"
            class="form-control"
            :class="{ 'is-invalid': errors.referral_email }"
            required
          />
          <div v-if="errors.referral_email" class="invalid-feedback">
            {{ errors.referral_email }}
          </div>
        </div>

        <div class="btn-center">
          <button
            type="submit"
            class="hortense-btn-primary"
            :disabled="isSubmitting"
          >
            <span class="text-nowrap" v-if="isSubmitting">Envoi en cours...</span>
            <span v-else class="text-nowrap">JE PARRAINE UN PROCHE</span>
          </button>
        </div>

        <div class="ou-separator">
          <span class="ou-text">OU</span>
        </div>

        <div class="btn-center">
          <a
            v-if="!isPhone"
            href="#"
            class="referral-link"
            @click.prevent="copyReferralLink"
          >
            {{ linkCopied ? 'Lien copié !' : "J'envoie un lien de parrainage" }}
          </a>
          <a
            v-else
            href="#"
            class="referral-link"
            @click.prevent="showShareOverlay = true"
          >
            J'envoie un lien de parrainage
          </a>
        </div>
      </form>

      <!-- Success/Error Messages -->
      <div v-if="successMessage" class="alert-success mt-4" role="alert">
        {{ successMessage }}
      </div>
      <div v-if="errorMessage" class="alert-danger mt-4" role="alert">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
  import fetcher from 'packs/shared/api.js';

  export default {
    name: 'my-sponsorship',
    props: ["user"],
    computed: {
      referralLink() {
        return `${window.location.origin}/inscription${this.user.id ? '?ref=' + this.user.id : ''}`;
      }
    },
    data() {
      return {
        activeTab: null,
        referralForm: {
          referral_first_name: '',
          referral_last_name: '',
          referral_email: ''
        },
        errors: {},
        isSubmitting: false,
        successMessage: '',
        errorMessage: '',
        sponsorPoints: 0,
        godchildPoints: 0,
        sponsorCount: 0,
        completedSponsorships: 0,
        exclusiveCodes: [],
        isPhone: window.innerWidth <= 768,
        linkCopied: false,
        showShareOverlay: false,
      }
    },
    mounted() {
      this.fetchSponsorshipData();
      window.addEventListener('resize', this.handleResize);
    },
    beforeUnmount() {
      window.removeEventListener('resize', this.handleResize);
    },
    methods: {
      async fetchSponsorshipData() {
        try {
          // Fetch sponsorship stats
          const response = await fetcher.get(`/sponsorships?user_id=${this.user.id}`);
          if (response.data && response.data.stats) {
            const stats = response.data.stats;
            this.sponsorPoints = stats.total_points_earned || 0;
            this.godchildPoints = stats.total_referral_points_earned || 0;
            this.sponsorCount = stats.total_referrals || 0;
            this.completedSponsorships = stats.completed_sponsorships || 0;
          }

          // Fetch discount codes separately
          const codesResponse = await fetcher.get('/sponsorships/discount_codes');
          if (codesResponse.data && codesResponse.data.discount_codes) {
            this.exclusiveCodes = codesResponse.data.discount_codes.filter(code => code.active) || [];
          }
        } catch (error) {
          console.error('Error fetching sponsorship data:', error);
        }
      },

      async submitReferral() {
        this.isSubmitting = true;
        this.errors = {};
        this.successMessage = '';
        this.errorMessage = '';

        try {
          const response = await fetcher.post('/sponsorships', {
            sponsorship: this.referralForm
          });

          if (response.data.success) {
            this.successMessage = response.data.message || 'Invitation envoyée avec succès!';
            this.resetForm();
            this.fetchSponsorshipData();
          } else {
            this.errorMessage = 'Une erreur est survenue lors de l\'envoi de l\'invitation.';
          }
        } catch (error) {
          if (error.response && error.response.data && error.response.data.errors) {
            this.errorMessage = error.response.data.errors[0];
          } else {
            this.errorMessage = 'Une erreur est survenue lors de l\'envoi de l\'invitation.';
          }
        } finally {
          this.isSubmitting = false;
        }
      },

      resetForm() {
        this.referralForm = {
          referral_first_name: '',
          referral_last_name: '',
          referral_email: ''
        };
      },

      async copyReferralLink() {
        try {
          await navigator.clipboard.writeText(this.referralLink);
          this.linkCopied = true;
          setTimeout(() => { this.linkCopied = false; }, 2500);
        } catch {
          // Fallback for older browsers
          const el = document.createElement('textarea');
          el.value = this.referralLink;
          document.body.appendChild(el);
          el.select();
          document.execCommand('copy');
          document.body.removeChild(el);
          this.linkCopied = true;
          setTimeout(() => { this.linkCopied = false; }, 2500);
        }
      },

      async shareReferralLink() {
        this.showShareOverlay = false;
        if (navigator.share) {
          try {
            await navigator.share({
              title: "Je t'invite à découvrir Hortense 🌿",
              text: 'En rejoignant le Club Hortense via mon lien, reçois 100 points de fidélité qui te permettront de débloquer des privilèges exclusifs pour tes prochains séjours ✨',
              url: this.referralLink
            });
          } catch {
            await this.copyReferralLink();
          }
        } else {
          // Fallback: copy to clipboard on mobile if Web Share API unavailable
          await this.copyReferralLink();
        }
      },

      handleResize() {
        this.isPhone = window.innerWidth <= 768;
      }
    }
  }
</script>

<style scoped>
#my-sponsorship {
  width: 100%;
  font-family: 'Raleway', sans-serif;
}

/* Page Title — matches .content-title in Registrationuser.vue */
.page-title {
  font-family: 'Merriweather', serif;
  font-size: 32px;
  font-weight: 400;
  color: #1a1a1a;
  margin-bottom: 16px;
}

/* Tabs Navigation */
.sponsorship-tabs {
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
}

.tab-link {
  font-size: 14px;
  color: #3B6152;
  text-decoration: underline;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s;
}

.tab-link.active {
  font-weight: 700;
  text-decoration-thickness: 2px;
}

.tab-link:hover {
  color: #2e4f42;
}

/* Main white card wrapper — matches .info-card in Registrationuser.vue */
.sponsorship-card {
  background: #fff;
  border-radius: 14px;
  padding: 24px 28px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* Intro text (default view) */
.intro-text {
  font-size: 15px;
  color: #333;
  line-height: 1.7;
  margin-bottom: 24px;
}

/* How it works title */
.how-it-works-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: 0.5px;
  margin-bottom: 24px;
}

/* Steps container */
.steps-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
}

/* Badges row */
.steps-badges-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding-left: 10%;
  padding-right: 10%;
}

.step-badge {
  background: #3B6152;
  color: #fff;
  padding: 8px 20px;
  border-radius: 25px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
}

.step-dashed-line {
  flex: 1;
  height: 0;
  border-top: 2px dashed #b0c4bb;
  margin: 0 8px;
  min-width: 40px;
}

/* Cards row */
.steps-cards-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.step-col {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.step-card {
  background: #e8f3f0;
  border-radius: 12px;
  padding: 20px 16px;
  text-align: center;
  flex: 1;
}

.step-title {
  font-size: 15px;
  font-weight: 600;
  color: #3B6152;
  margin-bottom: 10px;
  line-height: 1.4;
  border-bottom: 1px solid #fff;
  padding-bottom: 5px;
}

.step-desc {
  font-size: 14px;
  color: #333;
  line-height: 1.6;
  margin: 0;
}

/* Card section title — Merriweather weight 400 like .content-title */
.card-section-title {
  font-family: 'Merriweather', serif;
  font-size: 28px;
  font-weight: 400;
  color: #1a1a1a;
  margin-bottom: 8px;
}

.card-section-desc {
  font-size: 15px;
  color: #333;
  margin-bottom: 28px;
  line-height: 1.6;
}

/* Points Cards Grid */
.points-cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 32px;
}

.points-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5e3df;
  overflow: hidden;
  text-align: center;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
}

.points-card-header {
  border-bottom: 1px solid #e5e3df;
  padding: 14px 16px;
}

.points-card-label {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #3B6152;
}

.points-card-body {
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.points-value {
  font-size: 48px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1;
}

.points-unit {
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #000;
  margin-bottom: 6px;
}

.points-desc {
  font-size: 13px;
  color: #5D6561;
  line-height: 1.6;
}

.no-codes {
  text-align: center;
  padding: 8px 0;
}

.no-codes-main {
  font-size: 13px;
  color: #5D6561;
  margin: 0 0 10px 0;
}

.no-codes-sub {
  font-size: 13px;
  font-weight: 500;
  color: #000;
  margin: 0;
  line-height: 1.5;
}

.codes-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.code-item {
  background: #f5f5f5;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e5e3df;
}

.code-text {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  font-family: 'Courier New', monospace;
}

/* Referral Form — matches form styles in Registrationuser.vue */
.referral-form {
  max-width: 75%;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 10px;
  margin-bottom: 0;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
}

.form-group-full {
  /* full width below the row */
}

.form-label {
  font-size: 15px;
  font-weight: 500;
  color: #000;
  margin-bottom: 0;
}

/* Matches .form-group input in Registrationuser.vue */
.form-control {
  border: 1.5px solid #e5e3df;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  color: #000;
  background: #fff;
  outline: none;
  transition: border-color 0.18s;
  font-family: 'Raleway', sans-serif;
  width: 100%;
  box-sizing: border-box;
}

.form-control:focus {
  border-color: #3B6152;
  box-shadow: 0 0 0 3px rgba(59, 97, 82, 0.08);
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  font-size: 13px;
  color: #dc3545;
  margin-top: 2px;
}

/* Button — matches .btn-primary-dashboard style */
.btn-center {
  text-align: center;
  margin-top: 20px;
}

.hortense-btn-primary {
  background: #3B6152;
  border: none;
  color: #fff;
  padding: 14px 48px;
  border-radius: 8px;
  text-decoration: none;
  display: inline-block;
  transition: background 0.2s, opacity 0.2s;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  font-family: 'Raleway', sans-serif;
}

.hortense-btn-primary:hover {
  background: #2e4f42;
}

.hortense-btn-primary:disabled {
  background: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Alerts */
.mt-4 {
  margin-top: 16px;
}

.alert-success {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #3B6152;
  border-radius: 8px;
  padding: 14px 16px;
  font-size: 14px;
}

.alert-danger {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  border-radius: 8px;
  padding: 14px 16px;
  font-size: 14px;
}

/* =====================
   RESPONSIVE
   ===================== */
@media (max-width: 900px) {
  .points-cards-grid {
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }
}

@media (max-width: 767px) {
  .page-title {
    font-size: 24px;
  }

  .sponsorship-card {
    padding: 20px 16px;
    border-radius: 12px;
  }

  /* Steps: stack vertically on mobile */
  .steps-container {
    gap: 16px;
  }

  .steps-badges-row {
    flex-direction: column;
    gap: 12px;
  }

  /* Hide dashed line on mobile */
  .step-dashed-line {
    display: none;
  }

  .steps-cards-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .step-card {
    text-align: left;
    padding: 16px 14px;
  }

  .step-title {
    text-align: left;
  }

  .step-desc {
    text-align: left;
  }

  /* Mobile vertical stack layout */
  .steps-container-mobile {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 32px;
  }

  .step-mobile-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  .step-badge-mobile {
    background: #3B6152;
    color: #fff;
    padding: 8px 20px;
    border-radius: 25px;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    white-space: nowrap;
    display: inline-block;
    width: fit-content;
  }

  /* Points cards: single column on mobile */
  .points-cards-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  /* Form row: single column on mobile */
  .form-row {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .hortense-btn-primary {
    width: 100%;
    max-width: 340px;
  }

  .card-section-title {
    font-size: 22px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 22px;
  }

  .sponsorship-tabs {
    gap: 16px;
  }

  .tab-link {
    font-size: 13px;
  }

  .points-value {
    font-size: 40px;
  }
}

/* OU Separator */
.ou-separator {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 20px auto;
  max-width: 75%;
}

.ou-separator::before,
.ou-separator::after {
  content: '';
  flex: 1;
  border-top: 1px solid #e5e3df;
}

.ou-separator::before {
  margin-right: 12px;
}

.ou-separator::after {
  margin-left: 12px;
}

.ou-text {
  font-size: 13px;
  font-weight: 600;
  color: #5D6561;
  letter-spacing: 1px;
  text-transform: uppercase;
  white-space: nowrap;
}

/* Referral link */
.referral-link {
  font-size: 14px;
  font-weight: 500;
  color: #3B6152;
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.2s;
  font-family: 'Raleway', sans-serif;
}

.referral-link:hover {
  color: #2e4f42;
}

/* =====================
   MOBILE SHARE OVERLAY (bottom sheet)
   ===================== */
.share-overlay-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.share-overlay-sheet {
  background: #fff;
  border-radius: 20px 20px 0 0;
  padding: 28px 24px 36px;
  width: 100%;
  max-width: 480px;
  position: relative;
  text-align: center;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.12);
}

.share-overlay-close {
  position: absolute;
  top: 16px;
  right: 18px;
  background: none;
  border: none;
  font-size: 18px;
  color: #5D6561;
  cursor: pointer;
  line-height: 1;
  padding: 4px;
}

.share-overlay-title {
  font-family: 'Merriweather', serif;
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 6px;
  line-height: 1.3;
}

.share-overlay-points {
  font-size: 15px;
  font-weight: 600;
  color: #3B6152;
  margin-bottom: 16px;
  letter-spacing: 0.3px;
}

.share-overlay-desc {
  font-size: 14px;
  color: #333;
  line-height: 1.7;
  margin-bottom: 28px;
}

.share-overlay-cta {
  width: 100%;
  padding: 16px 24px;
  font-size: 14px;
}

/* Slide-up transition */
.overlay-slide-enter-active,
.overlay-slide-leave-active {
  transition: opacity 0.25s ease;
}

.overlay-slide-enter-active .share-overlay-sheet,
.overlay-slide-leave-active .share-overlay-sheet {
  transition: transform 0.3s ease;
}

.overlay-slide-enter-from,
.overlay-slide-leave-to {
  opacity: 0;
}

.overlay-slide-enter-from .share-overlay-sheet,
.overlay-slide-leave-to .share-overlay-sheet {
  transform: translateY(100%);
}
</style>
